import React, { useRef, useState, useEffect } from 'react';
import useCourseSegments from './useCourseSegments';
import CourseMapTrack from './CourseMapTrack';
import CourseMapLegend from './CourseMapLegend';
import CourseMapData from './CourseMapData';
import { RotateCw, RotateCcw, MoveRight, Layers, AlertCircle } from 'lucide-react';

/**
 * CourseMap
 * Orchestrator: fetches data, measures width, handles states,
 * and composes CourseMapTrack + CourseMapLegend.
 *
 * Props:
 *   courseId {number}  The course_id to fetch
 *   raceName {string}  For accessible label
 */
const CourseMap = ({ courseId, raceName }) => {
    const containerRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState(0);

    const { segments, meta, loading, error } = useCourseSegments(courseId);

    // ── Responsive width via ResizeObserver ──────────────────────────────────
    useEffect(() => {
        if (!containerRef.current) return;

        const observer = new ResizeObserver((entries) => {
            for (const entry of entries) {
                setContainerWidth(Math.floor(entry.contentRect.width));
            }
        });

        observer.observe(containerRef.current);
        setContainerWidth(Math.floor(containerRef.current.getBoundingClientRect().width));

        return () => observer.disconnect();
    }, []);

    // ── Helpers for header badges ────────────────────────────────────────────
    const getDirectionIcon = (direction) => {
        if (!direction) return null;
        const d = direction.toLowerCase();
        if (d.includes('clockwise') && !d.includes('counter')) return <RotateCw className="w-3.5 h-3.5" />;
        if (d.includes('counter') || d.includes('left')) return <RotateCcw className="w-3.5 h-3.5" />;
        return <MoveRight className="w-3.5 h-3.5" />;
    };

    const getTerrainBadgeClass = (terrain) => {
        if (!terrain) return 'bg-gray-700 text-gray-300';
        return terrain.toLowerCase() === 'turf'
            ? 'bg-green-900/60 text-green-400 border border-green-800/60'
            : 'bg-yellow-900/60 text-yellow-400 border border-yellow-800/60';
    };

    return (
        <div className="bg-gray-900 border border-gray-700/60 rounded-2xl overflow-hidden shadow-xl">

            {/* === Header === */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-800">
                <div className="flex items-center gap-2.5">
                    <div className="p-1.5 bg-indigo-500/20 rounded-lg">
                        <Layers className="w-4 h-4 text-indigo-400" />
                    </div>
                    <span className="text-sm font-bold text-gray-200 tracking-wide uppercase">
                        Course Map
                    </span>
                </div>

                {meta && (
                    <div className="flex items-center gap-2 text-xs font-mono">
                        <span className={`px-2 py-0.5 rounded-md uppercase font-bold ${getTerrainBadgeClass(meta.terrain)}`}>
                            {meta.terrain}
                        </span>
                        <span className="px-2 py-0.5 rounded-md bg-gray-800 text-gray-300 border border-gray-700">
                            {meta.meters}m
                        </span>
                        {meta.course && (
                            <span className="px-2 py-0.5 rounded-md bg-gray-800 text-gray-400 border border-gray-700">
                                {meta.course}
                            </span>
                        )}
                        {meta.direction && (
                            <span className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-gray-800 text-gray-400 border border-gray-700">
                                {getDirectionIcon(meta.direction)}
                                {meta.direction}
                            </span>
                        )}
                    </div>
                )}
            </div>

            {/* === Content Area === */}
            <div className="p-5" ref={containerRef}>

                {/* Loading skeleton */}
                {loading && (
                    <div className="animate-pulse space-y-3">
                        <div className="h-20 bg-gray-800 rounded-lg w-full" />
                        <div className="flex gap-2">
                            {[...Array(5)].map((_, i) => (
                                <div key={i} className="h-3 bg-gray-800 rounded w-16" />
                            ))}
                        </div>
                    </div>
                )}

                {/* Error state */}
                {!loading && error && (
                    <div className="flex items-center gap-3 text-sm text-red-400 bg-red-900/20 border border-red-800/40 rounded-xl p-4">
                        <AlertCircle className="w-5 h-5 flex-shrink-0" />
                        <span>Could not load course data: {error}</span>
                    </div>
                )}

                {/* D3 Track — renders when we have data and a measured width */}
                {!loading && !error && segments && meta && containerWidth > 0 && (
                    <>
                        <CourseMapTrack
                            meters={meta.meters}
                            segments={segments}
                            width={containerWidth}
                        />

                        <CourseMapLegend />

                        <CourseMapData segments={segments} meta={meta} />

                        {/* Thresholds row */}
                        {segments.Thresholds?.stats?.length > 0 && (
                            <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
                                <span className="text-gray-500 font-mono uppercase tracking-wider">
                                    Thresholds:
                                </span>
                                {segments.Thresholds.stats.map((stat) => (
                                    <span
                                        key={stat}
                                        className="px-2 py-0.5 rounded bg-violet-900/50 text-violet-300 border border-violet-800/50 font-mono font-bold"
                                    >
                                        {stat}
                                    </span>
                                ))}
                            </div>
                        )}

                        {/* Spurt description */}
                        {segments.Spurt?.description && segments.Spurt.description !== 'None' && (
                            <div className="mt-3 flex items-center gap-2 text-xs text-purple-300">
                                <span className="w-2 h-2 rounded-full bg-purple-400 inline-block" />
                                <span className="font-mono">
                                    Spurt zone — <span className="font-bold">{segments.Spurt.description}</span>
                                    {' '}({segments.Spurt.start}m → {segments.Spurt.end}m)
                                </span>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default CourseMap;
